/**
 * Standard resource code lookup for ETC modal dropdowns.
 *
 * Source: Resource Codes 2026.03.30.xlsx (Materials + Services sheets).
 * Materials codes are used for the Materials tab (cost types 53xxx).
 * Services codes are used for the Subcontractors tab (cost types 54xxx).
 * Rentals and Other tabs fall back to whatever resources the project data carries.
 *
 * Helpers filter the reference list to codes that actually have a value
 * in the current project (budget > 0 or present on an OC/CH/user row), so
 * the dropdown only shows relevant options.
 */

export const MATERIALS_RESOURCES = {
    AGG: 'Aggregate',
    AIC: 'Air Compressors',
    ANB: 'Anchor Bolts',
    AND: 'Anodes',
    ASP: 'Asphalt',
    BLA: 'Blasting Media',
    BKW: 'Bulk Water',
    RMU: 'Bullhorn',
    COA: 'Coating/Painting',
    CKE: 'Coke and Bags',
    CRT: 'Concrete',
    CON: 'Consumables',
    COL: 'Coolers (Eng. Equip)',
    CPA: 'CP / AC',
    DET: 'Detectors',
    ELE: 'Electrical',
    ERL: 'Equip Rental - Large',
    ERF: 'Equip Rental - Safety',
    ERS: 'Equip Rental - Small',
    ERT: 'Equip Rental - Tools',
    ESR: 'Equip-Service-Repair',
    FOP: 'Fabrication - Offsite Platform',
    FOS: 'Fabrication - Offsite Structur',
    FRG: 'Fasteners/Gaskets',
    FEN: 'Fencing',
    FIL: 'Filters',
    FIT: 'Fittings',
    FDH: 'Fleet Equipment Parts',
    FLU: 'Fleet Lubriants',
    FTP: 'Fleet Truck Parts',
    FUE: 'Fuel',
    FUT: 'Fuel - Tanks',
    FGH: 'Fuel Gas Heaters',
    FUL: 'Fuel Provider',
    GAU: 'Gauges',
    GEN: 'Generators',
    GRO: 'Grout',
    INT: 'Instrumentation',
    INE: 'Instrumentation/Electrical',
    JBX: 'J-Box',
    LIN: 'Liner',
    LUM: 'Lumber',
    MAL: 'Mats - Lease',
    MAP: 'Mats - Purchase',
    MEA: 'Measurement',
    MOB: 'Mobile Ofcs/Storage',
    NEO: 'Non-Equip Rental - Other',
    NES: 'Non-Equip Rental - Safety',
    NEJ: 'Non-Equip Rental - Sanitation',
    NET: 'Non-Equip Rental-OfficeTrailer',
    OFJ: 'OfcSvcs-Janitorial',
    OFS: 'Office Supplies',
    OTR: 'Other',
    PEC: 'PEC Buildings',
    PER: 'Permits',
    PPO: 'Pipe - Other',
    PPP: 'Pipe - PVC',
    PPS: 'Pipe - Steel',
    PSU: 'Pipe Supports',
    PLA: 'Platforms',
    POL: 'Poly/HDPE',
    PFB: 'Pre-Fab Buildings',
    PSE: 'Prof Services-EHS',
    PSO: 'Prof Services-Other',
    PUM: 'Pumps',
    REL: 'Real Estate Lease',
    REB: 'Rebar',
    RCT: 'Rectifier',
    SSS: 'Safety Supplies',
    STE: 'Safety Testing',
    SEE: 'Seeding/Hay',
    SKF: 'Skid Fabricators',
    SFW: 'Skids (wooden)',
    SMT: 'Small Tools',
    SGW: 'Spec Gas / Welding',
    STT: 'Storage Tanks',
    SST: 'Structural Steel',
    SBP: 'Sub Pole',
    SPP: 'SWPPP Materials',
    TIR: 'Tires',
    TWR: 'Towers',
    TRA: 'Transmitters',
    UTL: 'Utility Provider',
    VCR: 'Valves - Control & Regulators',
    VGE: 'Valves - General',
    VRE: 'Valves - Relief',
    VSP: 'Valves - Specialty',
    VNT: 'Vent',
    VES: 'Vessels (Eng. Equip)',
    WNI: 'Water and Ice',
};

export const SERVICES_RESOURCES = {
    BOR: 'Boring',
    CFN: 'Concrete-Foundation',
    CLR: 'Clearing-Tree/Shruberry',
    CRN: 'Crane Services',
    DRH: 'Drilling - HDD',
    FBF: 'Fabrication-Off Site',
    FNC: 'Fence Installation',
    HLM: 'Hauling-Material',
    HLQ: 'Hauling-Equipment',
    HYE: 'Hydro Excavation',
    HYT: 'Hydrotesting',
    'I&E': 'I&E (Instrument & Electrical)',
    TRE: 'Trenching',
    XRY: 'X-Ray/NDT',
    AER: 'Aerial/Drone Videography',
    ASB: 'Asbestos Remediation/Abatement',
    BER: 'Building Erection',
    BND: 'Bending',
    BOU: 'Building Erection and Build Ou',
    CAL: 'Caliper/Smart Picking',
    CAP: 'Concrete-Asphalt/Paving',
    CCT: 'Concrete-Coating',
    CLD: 'Cold Cut',
    CLN: 'Cleanup-Enviro/Fluid/Waste/Etc',
    CPM: 'Concrete-Pumping',
    CTF: 'Coating-Off Site',
    CTH: 'Cathodic Protection',
    CTN: 'Coating-On Site',
    DCV: 'DCVG-DirectCurrentVoltageGrade',
    DEW: 'Wellpoint/Dewatering',
    DRO: 'Drilling - Other',
    EMF: 'Equipment Maint-Off Site',
    EMN: 'Equipment Maint-On Site',
    FAC: 'Fiber Optic - Aerial Construct',
    FBN: 'Fabrication-On Site',
    FDD: 'Foundation-Drilling',
    FDP: 'Foundation-Piling',
    FDS: 'Foundation-Screw Anchor',
    FDT: 'Foundation-Torcsill',
    FFR: 'Fiber Optic - Fiber Placement',
    FIB: 'Fiber Optic - Underground Cons',
    FLR: 'Flaring',
    FLU: 'Flushing',
    FOG: 'Fuel/Oil/Grease',
    FRC: 'Frac Tank-Delivery & Install',
    FSF: 'Fusion-Off Site',
    FSN: 'Fusion-On Site',
    FSP: 'Fiber Optic - Splicing',
    GCN: 'Construct/Excavate-General',
    GRT: 'Grout Filling',
    HLE: 'Hauling-Escort',
    HLH: 'Hauling-Haz Materials',
    HLO: 'Hauling-Small Matl/Dirt/Aggr',
    HOT: 'Hot Tapping',
    HSG: 'Man-Camp Services',
    HTT: 'Heat Treatment',
    HYM: 'Hydromulching',
    INS: 'Insulation',
    ITF: 'Inspection & Testing-Off Site',
    ITN: 'Inspection & Testing-On Site',
    JAN: 'Janitorial - Offices',
    LDS: 'Landscaping Non-ROW activities',
    LKD: 'Leak Detection',
    LNR: 'Liners-Installation',
    LOF: 'Lube Oil Flush',
    MAR: 'Marine/Maritime',
    MDF: 'Medical Services-Off Site',
    MDN: 'Medical Services-On Site',
    NTR: 'Nitrogen Services',
    OFO: 'Office-Painting/Electrical/Etc',
    OFR: 'Office-Equipment Repair',
    OQF: 'OQs-Offsite',
    OQN: 'OQs-Onsite',
    OTH: 'Other',
    PFA: 'Professional-Office/Audit/F&A',
    PFC: 'Professional-Consulting',
    PFE: 'Professional-Engrg/Architect',
    PFI: 'Professional-IT Service',
    PFR: 'Professional-ROW Consulting',
    PIG: 'Pigging',
    PLB: 'Plumbing/Septic',
    PLW: 'Pipe Pillows/Trench Padding',
    PRG: 'Purging',
    RES: 'Rescue Services',
    RKB: 'Rock Blasting',
    SCF: 'Scaffolding',
    SDG: 'Seeding-ROW activities',
    SEC: 'Security',
    SNB: 'Sandblasting',
    SPA: 'Special Product Application',
    SVY: 'Surveying',
    SWP: 'Marsh/Swamp',
    TFC: 'Traffic Control',
    TLT: 'Portable Toilets/Trash Removal',
    TOW: 'Towing',
    TRF: 'Training-Office/Off Site',
    TRN: 'Training-On Site',
    TRQ: 'Torqueing',
    VAC: 'Vacuum Truck',
    WLD: 'Welding Crew',
    WTL: 'Weld Testing Lab',
    WWI: 'Waterwell Installation',
};

/**
 * Pick the reference resource map for a given tab.
 * Materials → Materials; Subcontractors → Services; everything else → union
 * so the modal still lists something sensible on Rentals/Other/All Third Party.
 */
export const referenceResourcesForTab = (tab) => {
    if (tab === 'Materials') return MATERIALS_RESOURCES;
    if (tab === 'Subcontractors') return SERVICES_RESOURCES;
    return { ...MATERIALS_RESOURCES, ...SERVICES_RESOURCES };
};

/**
 * Build the dropdown list for the ETC modal: code → description,
 * limited to the reference list for this tab AND the codes the project
 * actually uses (any positive budget or any OC/CH/user row).
 *
 * Falls back to the project's own codes for any code not in the reference
 * list, so nothing the user already has disappears from the dropdown.
 *
 * @param {Object} opts
 * @param {string} opts.tab                    – 'Materials', 'Subcontractors', etc.
 * @param {Object} opts.projectResources       – { code: description } built from project rows
 * @param {Object} opts.budgetByResource       – { code: amount } from etcLedger
 * @returns {Object} { code: description } for the dropdown
 */
export const buildResourceDropdown = ({ tab, projectResources = {}, budgetByResource = {} }) => {
    const reference = referenceResourcesForTab(tab);
    const hasValue = new Set();
    Object.entries(budgetByResource).forEach(([code, amt]) => {
        if (code && +amt > 0) hasValue.add(code);
    });
    Object.keys(projectResources).forEach(code => { if (code) hasValue.add(code); });

    const out = {};
    hasValue.forEach(code => {
        const desc = reference[code] || projectResources[code] || '';
        out[code] = desc;
    });
    return out;
};
